/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/announcementApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API functions for Announcements.
    Centralizes all API calls related to Announcements.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  Announcement,
  AnnouncementPayload,
  ListAnnouncementsResponse,
  AnnouncementResponse,
} from '../types';

export const ANNOUNCEMENTS_ENDPOINT = '/api/telephony/announcements';

/**
 * Generic API request helper
 */
async function requestAnnouncementsApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit,
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List Announcements with pagination and search
 */
export async function fetchAnnouncements(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string,
): Promise<ListAnnouncementsResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${ANNOUNCEMENTS_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch announcements');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as Announcement[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0,
    },
  };
}

/**
 * Get single Announcement by ID
 */
export async function fetchAnnouncement(id: string | number): Promise<Announcement> {
  const { data } = await requestAnnouncementsApi<Announcement>(
    `${ANNOUNCEMENTS_ENDPOINT}/${id}`,
    'Failed to fetch announcement',
  );
  return data;
}

/**
 * Create new Announcement
 */
export async function createAnnouncement(
  payload: AnnouncementPayload,
): Promise<Announcement> {
  const { data } = await requestAnnouncementsApi<Announcement>(
    ANNOUNCEMENTS_ENDPOINT,
    'Failed to create announcement',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Update Announcement
 */
export async function updateAnnouncement(
  id: string | number,
  payload: AnnouncementPayload,
): Promise<Announcement> {
  const { data } = await requestAnnouncementsApi<Announcement>(
    `${ANNOUNCEMENTS_ENDPOINT}/${id}`,
    'Failed to update announcement',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Delete Announcement
 */
export async function deleteAnnouncement(id: string | number): Promise<void> {
  await requestAnnouncementsApi<null>(
    `${ANNOUNCEMENTS_ENDPOINT}/${id}`,
    'Failed to delete announcement',
    {
      method: 'DELETE',
    },
  );
}

